"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Map, Building2, CalendarHeart, Clock, MapPin } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "50px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const events = [{
  title: "Ceremony",
  icon: Building2,
  date: "Saturday, September 14th, 2026",
  time: "4:00 PM",
  locationName: "The Grand Estate Gardens",
  address: "123 Wedding Lane, Love City, CA 90000",
  link: "/WeddingDetailsPage"
}, {
  title: "Reception",
  icon: Map,
  date: "Saturday, September 14th, 2026",
  time: "6:00 PM",
  locationName: "The Grand Estate Ballroom",
  address: "123 Wedding Lane, Love City, CA 90000",
  link: "/WeddingDetailsPage"
}];
export default function WhereWhen() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: cardsRef,
    isVisible: areCardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="where-when" className="bg-background py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_center,var(--color-primary)_0%,transparent_100%)] opacity-[0.02] pointer-events-none" />

      <div className="container mx-auto px-6 max-w-6xl relative z-10">
        <div ref={headerRef} className={`text-center mb-16 md:mb-24 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl text-foreground mb-6 tracking-tight">
            Where & When
          </h2>
          <div className="w-12 h-[1px] bg-border mx-auto mb-6"></div>
          <p className="text-muted-foreground max-w-2xl mx-auto font-light text-lg md:text-xl leading-relaxed">
            We can't wait to celebrate with you. Here are the details for our special day.
          </p>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {events.map((event, i) => {
          const Icon = event.icon;
          return <Card key={i} data-index={i} className={`bg-card/30 backdrop-blur-sm border-border/40 shadow-sm hover:shadow-md transition-all duration-700 ease-out p-8 md:p-12 flex flex-col items-center text-center group ${areCardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <div className="mb-8 p-5 rounded-2xl bg-background/50 border border-border/30 text-foreground/70 group-hover:text-foreground group-hover:scale-105 transition-all duration-500">
                  <Icon className="w-10 h-10 stroke-[1]" />
                </div>
                
                <h3 className="font-default text-3xl md:text-4xl text-foreground mb-8 tracking-wide">
                  {event.title}
                </h3>

                <div className="space-y-5 mb-12 flex-grow flex flex-col justify-center w-full">
                  <div className="flex items-center justify-center gap-3 text-muted-foreground">
                    <CalendarHeart className="w-4 h-4 stroke-[1.5]" />
                    <span className="font-light tracking-wide">{event.date}</span>
                  </div>
                  <div className="flex items-center justify-center gap-3 text-muted-foreground">
                    <Clock className="w-4 h-4 stroke-[1.5]" />
                    <span className="font-light tracking-wide">{event.time}</span>
                  </div>
                  
                  <div className="w-8 h-[1px] bg-border/50 mx-auto my-2"></div>
                  
                  <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 stroke-[1.5] hidden sm:block" />
                      <span className="font-medium text-foreground tracking-wide">{event.locationName}</span>
                    </div>
                    <span className="font-light text-sm opacity-80">{event.address}</span>
                  </div>
                </div>

                <Button variant="outline" className="rounded-lg border-border/60 hover:bg-primary hover:text-primary-foreground transition-colors w-full sm:w-auto px-10 py-6 font-default text-lg tracking-wider uppercase" asChild>
                  <Link to={event.link}>View Details</Link>
                </Button>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}